// src/services/offlineQueue.js
import localforage from "localforage";
import { addDoc, collection } from "firebase/firestore";
import { db } from "../firebase"; // adjust path to your firebase export
import { saveInvoice as remoteSaveInvoice } from "./invoiceService"; // optional: your existing saveInvoice

// configure a store for queued invoices
const QUEUE_STORE = "invoice_queue_v1";
localforage.config({ name: "billing_app", storeName: QUEUE_STORE });

// helper to sleep
const wait = (ms) => new Promise((res) => setTimeout(res, ms));

// exponential backoff generator (ms)
function backoffDelay(attempt) {
  const base = 1000; // 1s
  const max = 60 * 1000; // 60s
  const delay = Math.min(max, Math.floor(base * Math.pow(1.8, attempt)));
  // jitter +/- 30%
  const jitter = Math.floor(delay * (Math.random() * 0.6 - 0.3));
  return Math.max(0, delay + jitter);
}

/**
 * A queuedItem structure:
 * {
 *   id: string, // client idempotency token (uuid or invoiceId)
 *   type: "invoice",
 *   payload: { ... }, // invoice payload (same shape as saveInvoice expects)
 *   attempts: 0,
 *   createdAt: ISOString,
 *   nextTryAt: ISOString (optional)
 * }
 */

// generate simple unique id
function genId() {
  return `q_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/* ---------- Queue helpers ---------- */

export async function enqueueInvoice(payload, opts = {}) {
  // payload should contain an identifier if available (e.g., invoiceId or voucherNo) for idempotency
  const keyId = opts.clientId || payload.clientToken || payload.invoiceId || genId();
  const queueItem = {
    id: keyId,
    type: "invoice",
    payload,
    attempts: 0,
    createdAt: new Date().toISOString(),
    nextTryAt: null,
  };
  // store under key = id so duplicates can be detected/overwritten
  await localforage.setItem(keyId, queueItem);
  return queueItem;
}

export async function dequeueItem(id) {
  try {
    await localforage.removeItem(id);
    return true;
  } catch (e) {
    console.warn("dequeueItem failed", e);
    return false;
  }
}

export async function getAllQueueItems() {
  const result = [];
  await localforage.iterate((value, key) => {
    // only include keys starting with q_ or that match our structure
    result.push(value);
  });
  // sort by createdAt ascending
  result.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  return result;
}

export async function getQueueCount() {
  let c = 0;
  await localforage.iterate(() => { c++; });
  return c;
}

/* ---------- Processor ---------- */

let processing = false;
let stopRequested = false;

export function stopProcessing() {
  stopRequested = true;
}

export function startProcessing(options = {}) {
  // options: { intervalMs, continueOnError }
  if (processing) return;
  processing = true;
  stopRequested = false;
  _processLoop(options).catch((err) => {
    console.error("queue processing crashed:", err);
  });
}

async function _processLoop({ intervalMs = 2000, continueOnError = true } = {}) {
  while (!stopRequested) {
    // only run when online
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      // wait until online event triggers retry
      await wait(3000);
      continue;
    }

    const items = await getAllQueueItems();
    if (!items || items.length === 0) {
      // idle wait
      await wait(intervalMs);
      continue;
    }

    // try first item (FIFO)
    const item = items[0];
    const now = Date.now();
    if (item.nextTryAt && new Date(item.nextTryAt).getTime() > now) {
      // not yet time to retry, wait to next
      await wait(500);
      continue;
    }

    try {
      await processQueueItem(item);
      // on success remove
      await dequeueItem(item.id);
      // small pause before next
      await wait(300);
    } catch (err) {
      console.warn("processQueueItem failed", err);
      // increment attempts and set nextTryAt
      item.attempts = (item.attempts || 0) + 1;
      const delay = backoffDelay(item.attempts);
      item.nextTryAt = new Date(Date.now() + delay).toISOString();
      // persist updated item
      await localforage.setItem(item.id, item);
      if (!continueOnError) throw err;
      // small wait
      await wait(300);
    }
  }
  processing = false;
}

/* ---------- Core item processor ---------- */

async function processQueueItem(item) {
  if (!item || !item.type) throw new Error("Invalid queue item");
  if (item.type === "invoice") {
    return await _processInvoiceQueueItem(item);
  }
  throw new Error("Unsupported queue item type: " + item.type);
}

async function _processInvoiceQueueItem(item) {
  // attempt to send to server. Prefer calling your existing saveInvoice function
  // which likely handles server logic. If not available, fallback to direct addDoc.
  try {
    // 1) If you exported `remoteSaveInvoice`, call it (it should be an async fn that returns remote id)
    if (typeof remoteSaveInvoice === "function") {
      // give remoteSaveInvoice the payload; it should throw on failure
      const result = await remoteSaveInvoice(item.payload);
      // result might contain { id, voucherNo } etc.
      return result;
    }
  } catch (err) {
    // log then fallback to direct Firestore addDoc
    console.warn("remoteSaveInvoice failed, will try addDoc fallback:", err);
  }

  throw new Error(
    "Offline queue no longer writes to invoices. Save from Billing Find (sales import) while online."
  );
}
